/**
 * Single WebSocket connection for the whole app.
 *
 * Opens against `/piggy/ws?token=<JWT>` once the user is authenticated,
 * dispatches incoming messages to subscribed handlers, reconnects with
 * capped exponential backoff. Pages call `useWsSubscribe(type, handler)`
 * to listen for a specific message type.
 *
 * The backend sends:
 *   - `connected` — handshake echo with the bound principal
 *   - `pnl_update` — equity / unrealized / daily P&L tick
 *   - `tick` — simulated market tick
 *   - `fill` — order fill (engine-level broadcast)
 *   - `alert` — info / warning / error / success notifications
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { TokenStore } from "@/api/http";
import { useAuth } from "@/auth/AuthContext";

type WsHandler = (msg: WsMessage) => void;

export interface WsMessage {
  type: string;
  [key: string]: unknown;
}

interface WsValue {
  state: "idle" | "connecting" | "open" | "closed";
  subscribe: (type: string, handler: WsHandler) => () => void;
}

const Ctx = createContext<WsValue | null>(null);

export function useWebSocketState(): WsValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useWebSocketState used outside WebSocketProvider");
  return v;
}

/** Subscribe to messages of a given `type`. Returns an unsubscriber. */
export function useWsSubscribe(type: string, handler: WsHandler) {
  const { subscribe } = useWebSocketState();
  const cb = useRef(handler);
  cb.current = handler;
  useEffect(() => subscribe(type, (msg) => cb.current(msg)), [type, subscribe]);
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<WsValue["state"]>("idle");
  // Map<type, Set<handler>>
  const handlers = useRef<Map<string, Set<WsHandler>>>(new Map());
  const sock = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<number | null>(null);

  // Stable subscribe function so consumers' useEffect deps don't change.
  const subscribe = useMemo<WsValue["subscribe"]>(
    () => (type, handler) => {
      let bucket = handlers.current.get(type);
      if (!bucket) {
        bucket = new Set();
        handlers.current.set(type, bucket);
      }
      bucket.add(handler);
      return () => {
        const b = handlers.current.get(type);
        if (b) {
          b.delete(handler);
          if (b.size === 0) handlers.current.delete(type);
        }
      };
    },
    [],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      cleanup();
      setState("idle");
      return;
    }

    let alive = true;

    function connect() {
      if (!alive) return;
      const token = TokenStore.get();
      if (!token) return;

      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${proto}//${window.location.host}/piggy/ws?token=${encodeURIComponent(token)}`;

      setState("connecting");
      const ws = new WebSocket(url);
      sock.current = ws;

      ws.addEventListener("open", () => {
        if (!alive) {
          ws.close();
          return;
        }
        reconnectAttempt.current = 0;
        setState("open");
      });

      ws.addEventListener("message", (event) => {
        if (!alive) return;
        let parsed: WsMessage;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }
        const bucket = handlers.current.get(parsed.type);
        if (bucket) {
          for (const h of bucket) {
            try {
              h(parsed);
            } catch (err) {
              // Don't let one bad handler kill the dispatch loop.
              console.error("ws handler threw:", err);
            }
          }
        }
      });

      ws.addEventListener("close", (event) => {
        if (!alive) return;
        setState("closed");
        sock.current = null;

        // 4401 = backend rejected our token. Don't reconnect; the auth
        // expiry path will route to /login.
        if (event.code === 4401) {
          window.dispatchEvent(new CustomEvent("piggy:auth-expired"));
          return;
        }

        // Capped exponential backoff: 0.5s, 1s, 2s, … up to 30s.
        const delay = Math.min(30_000, 500 * 2 ** reconnectAttempt.current);
        reconnectAttempt.current += 1;
        reconnectTimer.current = window.setTimeout(connect, delay);
      });

      ws.addEventListener("error", () => {
        // Browser fires `error` then `close` — we handle reconnection in close.
      });
    }

    function cleanup() {
      if (reconnectTimer.current !== null) {
        window.clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      if (sock.current) {
        try {
          sock.current.close();
        } catch {
          // ignore — closing an already-closed socket is fine
        }
        sock.current = null;
      }
    }

    connect();
    return () => {
      alive = false;
      cleanup();
    };
  }, [isAuthenticated]);

  const value: WsValue = { state, subscribe };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
